import React from 'react';
import { Box, Chip, FormHelperText, Tooltip, Typography } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useTheme } from '@mui/material/styles';
import { SearchableMultiSelect } from 'components/common/SearchableMultiSelect';
import studioStyles from '../environments/StudioModal.module.scss';

/** Validation banner colors aligned with InspectorPanel.tsx (EDITED_HIGHLIGHT / EDIT_BORDER). */
const VALIDATION_BANNER_BG = '#ffd3a3';
const VALIDATION_BANNER_BORDER = '#ffc88c';

export type ProjectConfigFilterOptions = {
  /** Level display strings (e.g. PILOT, CLUSTER, PILOT.WEB_DASHBOARD) — data-derived, same as ConfigComparePage */
  levels: string[];
  clusters: Array<{ value: string; label: string }>;
  applications: Array<{ value: string; label: string }>;
  fuelTypes: Array<{ value: string; label: string }>;
  configTypes: string[];
  configKeys: string[];
  components: string[];
};

export type ProjectConfigFilterProps = {
  filterOptions: ProjectConfigFilterOptions;
  levelFilter: string[];
  clusterFilter: string[];
  applicationFilter: string[];
  fuelTypeFilter: string[];
  configTypes: string[];
  configKeyFilter: string[];
  componentFilter: string[];
  onLevelFilterChange: (value: string[]) => void;
  onClusterFilterChange: (value: string[]) => void;
  onApplicationFilterChange: (value: string[]) => void;
  onFuelTypeFilterChange: (value: string[]) => void;
  onConfigTypesChange: (value: string[]) => void;
  onConfigKeyFilterChange: (value: string[]) => void;
  onComponentFilterChange: (value: string[]) => void;
  onResetAll: () => void;
  onPageReset?: () => void;
  clusterLoading?: boolean;
  includeConfigType?: boolean;
  buttonSize?: 'sm' | 'md';
  /** When true, only the filter button is rendered here; use ProjectConfigFilterActiveChips below search bar. */
  activeFiltersBelow?: boolean;
  /** Scope actually sent to the API (one per request). Chips show only this scope so UI matches the request. */
  effectiveApiScope?: 'cluster' | 'product_type' | 'fuel_type' | 'pilot';
};

export function ProjectConfigFilter({
  filterOptions,
  levelFilter = [],
  clusterFilter = [],
  applicationFilter = [],
  fuelTypeFilter = [],
  configTypes = [],
  configKeyFilter = [],
  componentFilter = [],
  onLevelFilterChange,
  onClusterFilterChange,
  onApplicationFilterChange,
  onFuelTypeFilterChange,
  onConfigTypesChange,
  onConfigKeyFilterChange,
  onComponentFilterChange,
  onResetAll,
  onPageReset,
  clusterLoading = false,
  includeConfigType = true,
  buttonSize = 'md',
  activeFiltersBelow = false,
  effectiveApiScope,
}: ProjectConfigFilterProps) {
  const theme = useTheme();
  const palette = theme.palette;
  const [filtersPanelOpen, setFiltersPanelOpen] = React.useState(false);

  // Draft filter state: only applied to parent when user clicks Apply. Empty = show all.
  const [draftLevelFilter, setDraftLevelFilter] = React.useState<string[]>(() => levelFilter ?? []);
  const [draftClusterFilter, setDraftClusterFilter] = React.useState<string[]>(clusterFilter ?? []);
  const [draftApplicationFilter, setDraftApplicationFilter] = React.useState<string[]>(applicationFilter ?? []);
  const [draftFuelTypeFilter, setDraftFuelTypeFilter] = React.useState<string[]>(fuelTypeFilter ?? []);
  const [draftConfigTypes, setDraftConfigTypes] = React.useState<string[]>(configTypes ?? []);
  const [draftConfigKeyFilter, setDraftConfigKeyFilter] = React.useState<string[]>(configKeyFilter ?? []);
  const [draftComponentFilter, setDraftComponentFilter] = React.useState<string[]>(componentFilter ?? []);

  // When panel opens, sync draft from current applied filters (props). Empty = show all.
  React.useEffect(() => {
    if (filtersPanelOpen) {
      const levels = levelFilter ?? [];
      setDraftLevelFilter(levels);
      setDraftClusterFilter(clusterFilter ?? []);
      setDraftApplicationFilter(applicationFilter ?? []);
      setDraftFuelTypeFilter(fuelTypeFilter ?? []);
      setDraftConfigTypes(configTypes ?? []);
      setDraftConfigKeyFilter(configKeyFilter ?? []);
      setDraftComponentFilter(componentFilter ?? []);
    }
  }, [filtersPanelOpen, levelFilter, clusterFilter, applicationFilter, fuelTypeFilter, configTypes, configKeyFilter, componentFilter]);

  const hasClusterLevelSelected = (draftLevelFilter ?? []).some((l) => l.toUpperCase() === 'CLUSTERS');
  const hasApplicationsLevelSelected = (draftLevelFilter ?? []).some((l) => l.toUpperCase() === 'APPLICATIONS');
  const hasFuelLevelSelected = (draftLevelFilter ?? []).some((l) => l.toUpperCase() === 'FUEL');
  const hasFuelTypeChosen = (draftFuelTypeFilter?.length ?? 0) > 0;

  // Scope options available (exclude empty/All placeholders)
  const applicationOptionsAvailable = (filterOptions.applications ?? []).filter((a) => (a.value ?? '').trim()).length > 0;
  const clusterOptionsAvailable = (filterOptions.clusters ?? []).length > 0;
  const fuelOptionsAvailable = (filterOptions.fuelTypes ?? []).filter((f) => f.value && f.value !== 'All').length > 0;

  // Show Application when: APPLICATIONS level, or FUEL level (pick app first), or CLUSTERS level (app required when fuel type selected), or whenever a fuel type is selected
  const showApplicationSection =
    hasApplicationsLevelSelected || hasFuelLevelSelected || hasClusterLevelSelected || hasFuelTypeChosen;

  // Validation: application is mandatory when APPLICATIONS level is selected; or when FUEL level is selected (user must choose application first); or when any fuel type is selected (including with CLUSTERS level)
  const needsApplicationSelection =
    applicationOptionsAvailable &&
    (draftApplicationFilter?.length ?? 0) === 0 &&
    (hasApplicationsLevelSelected || hasFuelLevelSelected || hasFuelTypeChosen);
  const needsClusterSelection = hasClusterLevelSelected && clusterOptionsAvailable && (draftClusterFilter?.length ?? 0) === 0 && !clusterLoading;
  const needsFuelSelection = hasFuelLevelSelected && fuelOptionsAvailable && (draftFuelTypeFilter?.length ?? 0) === 0;

  // When FUEL level is selected, user must choose Application before selecting Fuel Type (enforce "application first")
  const applicationRequiredBeforeFuel = hasFuelLevelSelected && applicationOptionsAvailable && (draftApplicationFilter?.length ?? 0) === 0;

  const validationErrors = React.useMemo(() => {
    const errors: string[] = [];
    if (needsApplicationSelection)
      errors.push(
        hasFuelTypeChosen
          ? 'Select at least one application (required when a fuel type is selected).'
          : 'Select at least one application to view application-level configs.'
      );
    if (needsClusterSelection) errors.push('Select at least one cluster to view cluster-level configs.');
    if (needsFuelSelection) errors.push('Select at least one fuel type to view fuel-level configs.');
    return errors;
  }, [needsApplicationSelection, needsClusterSelection, needsFuelSelection, hasFuelTypeChosen]);

  const canApplyFilters = validationErrors.length === 0;

  // When effectiveApiScope is set, only count the scope that was sent to the API (so badge/chips match the request).
  const scopeCount =
    effectiveApiScope === 'cluster' && (clusterFilter ?? []).length
      ? 1
      : effectiveApiScope === 'product_type' && (applicationFilter ?? []).length
        ? 1
        : effectiveApiScope === 'fuel_type' && (fuelTypeFilter ?? []).length
          ? 1
          : !effectiveApiScope
            ? ((clusterFilter ?? []).length ? 1 : 0) + ((applicationFilter ?? []).length ? 1 : 0) + ((fuelTypeFilter ?? []).length ? 1 : 0)
            : 0;
  const activeFilterCount =
    ((levelFilter ?? []).length ? 1 : 0) +
    scopeCount +
    (includeConfigType && (configTypes ?? []).length ? 1 : 0) +
    ((configKeyFilter ?? []).length ? 1 : 0) +
    ((componentFilter ?? []).length ? 1 : 0);

  const hasActiveFilters =
    (levelFilter ?? []).length > 0 ||
    (clusterFilter ?? []).length > 0 ||
    (applicationFilter ?? []).length > 0 ||
    (fuelTypeFilter ?? []).length > 0 ||
    (includeConfigType && (configTypes ?? []).length > 0) ||
    (configKeyFilter ?? []).length > 0 ||
    (componentFilter ?? []).length > 0;

  const toggleConfigType = React.useCallback(
    (value: string, add: boolean) => {
      const next = add
        ? (configTypes.includes(value) ? configTypes : [...configTypes, value])
        : configTypes.filter((x) => x !== value);
      onConfigTypesChange(next);
      onPageReset?.();
    },
    [configTypes, onConfigTypesChange, onPageReset]
  );

  const handleApplyFilters = React.useCallback(() => {
    const levelsToApply = draftLevelFilter ?? [];
    onLevelFilterChange(levelsToApply);
    onClusterFilterChange(draftClusterFilter);
    onApplicationFilterChange(draftApplicationFilter);
    onFuelTypeFilterChange((draftApplicationFilter?.length ?? 0) === 0 ? [] : draftFuelTypeFilter);
    onConfigTypesChange(draftConfigTypes);
    onConfigKeyFilterChange(draftConfigKeyFilter);
    onComponentFilterChange(draftComponentFilter);
    onPageReset?.();
    setFiltersPanelOpen(false);
  }, [
    draftLevelFilter,
    draftClusterFilter,
    draftApplicationFilter,
    draftFuelTypeFilter,
    draftConfigTypes,
    draftConfigKeyFilter,
    draftComponentFilter,
    onLevelFilterChange,
    onClusterFilterChange,
    onApplicationFilterChange,
    onFuelTypeFilterChange,
    onConfigTypesChange,
    onConfigKeyFilterChange,
    onComponentFilterChange,
    onPageReset,
  ]);

  /** Reset all filters (show full list). Parent state is cleared; draft syncs from props when panel reopens. */
  const handleResetAndClose = React.useCallback(() => {
    onResetAll();
    setFiltersPanelOpen(false);
  }, [onResetAll]);

  const buttonClassName = `${studioStyles.button} ${studioStyles.outlineButton}`;
  const buttonStyle: React.CSSProperties =
    buttonSize === 'sm' ? { height: 32, padding: '0 12px', fontSize: 13 } : {};

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: activeFiltersBelow ? 'stretch' : 'flex-start', gap: 1 }}>
        <Box sx={{ position: 'relative', display: 'inline-flex' }}>
          <button
          type="button"
          className={buttonClassName}
          onClick={() => setFiltersPanelOpen(true)}
          style={{ ...buttonStyle, position: 'relative', paddingRight: activeFilterCount > 0 ? 28 : undefined }}
        >
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
            <FilterListIcon sx={{ fontSize: 18 }} />
            Filters
          </span>
          {activeFilterCount > 0 ? (
            <Box
              sx={{
                position: 'absolute',
                top: 4,
                right: 6,
                minWidth: 20,
                height: 20,
                borderRadius: '50%',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                fontSize: 11,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              {activeFilterCount}
            </Box>
          ) : null}
        </button>
      </Box>

      {hasActiveFilters && !activeFiltersBelow && (
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 1,
            minHeight: 32,
            width: '100%',
            px: 0.5,
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              color: 'text.secondary',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
              mr: 0.5,
            }}
          >
            Active Filters:
          </Typography>
          {levelFilter.length > 0 &&
            (levelFilter.length <= 2
              ? levelFilter.map((lv) => (
                  <Chip
                    key={`level-${lv}`}
                    size="small"
                    label={`Level: ${lv}`}
                    onDelete={() => onLevelFilterChange(levelFilter.filter((x) => x !== lv))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip title={levelFilter.join(', ')} placement="top">
                    <Chip
                      size="small"
                      label={`Level: ${levelFilter.length} selected`}
                      onDelete={() => onLevelFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          {clusterFilter.length > 0 &&
            (clusterFilter.length <= 2
              ? clusterFilter.map((v) => (
                  <Chip
                    key={`cluster-${v}`}
                    size="small"
                    label={`Cluster: ${filterOptions.clusters.find((c) => c.value === v)?.label ?? v}`}
                    onDelete={() => onClusterFilterChange(clusterFilter.filter((x) => x !== v))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip
                    title={clusterFilter.map((v) => filterOptions.clusters.find((c) => c.value === v)?.label ?? v).join(', ')}
                    placement="top"
                  >
                    <Chip
                      size="small"
                      label={`Cluster: ${clusterFilter.length} selected`}
                      onDelete={() => onClusterFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          {applicationFilter.length > 0 &&
            (applicationFilter.length <= 2
              ? applicationFilter.map((v) => (
                  <Chip
                    key={`app-${v}`}
                    size="small"
                    label={`Application: ${v}`}
                    onDelete={() => onApplicationFilterChange(applicationFilter.filter((x) => x !== v))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip title={applicationFilter.join(', ')} placement="top">
                    <Chip
                      size="small"
                      label={`Application: ${applicationFilter.length} selected`}
                      onDelete={() => onApplicationFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          {fuelTypeFilter.length > 0 &&
            (fuelTypeFilter.length <= 2
              ? fuelTypeFilter.map((v) => (
                  <Chip
                    key={`fuel-${v}`}
                    size="small"
                    label={`Fuel: ${v}`}
                    onDelete={() => onFuelTypeFilterChange(fuelTypeFilter.filter((x) => x !== v))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip title={fuelTypeFilter.join(', ')} placement="top">
                    <Chip
                      size="small"
                      label={`Fuel: ${fuelTypeFilter.length} selected`}
                      onDelete={() => onFuelTypeFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          {includeConfigType &&
            configTypes.map((v) => (
              <Chip
                key={`t-${v}`}
                size="small"
                label={`Type: ${v}`}
                onDelete={() => toggleConfigType(v, false)}
                sx={{ height: 28 }}
              />
            ))}
          {configKeyFilter.length > 0 &&
            (configKeyFilter.length <= 2
              ? configKeyFilter.map((v) => (
                  <Chip
                    key={`key-${v}`}
                    size="small"
                    label={`Config Key: ${v}`}
                    onDelete={() => onConfigKeyFilterChange(configKeyFilter.filter((x) => x !== v))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip title={configKeyFilter.join(', ')} placement="top">
                    <Chip
                      size="small"
                      label={`Config Key: ${configKeyFilter.length} selected`}
                      onDelete={() => onConfigKeyFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          {componentFilter.length > 0 &&
            (componentFilter.length <= 2
              ? componentFilter.map((v) => (
                  <Chip
                    key={`comp-${v}`}
                    size="small"
                    label={`Component: ${v}`}
                    onDelete={() => onComponentFilterChange(componentFilter.filter((x) => x !== v))}
                    sx={{ height: 28 }}
                  />
                ))
              : (
                  <Tooltip title={componentFilter.join(', ')} placement="top">
                    <Chip
                      size="small"
                      label={`Component: ${componentFilter.length} selected`}
                      onDelete={() => onComponentFilterChange([])}
                      sx={{ height: 28 }}
                    />
                  </Tooltip>
                ))}
          <Typography
            component="button"
            variant="caption"
            sx={{
              fontWeight: 600,
              color: 'primary.main',
              textDecoration: 'underline',
              cursor: 'pointer',
              border: 0,
              background: 'none',
              p: 0,
            }}
            onClick={onResetAll}
          >
            Reset All
          </Typography>
        </Box>
      )}

      </Box>

      {filtersPanelOpen && (
        <>
          <div
            role="presentation"
            style={{
              position: 'fixed',
              inset: 0,
              zIndex: 1300,
              backgroundColor: 'rgba(0,0,0,0.5)',
            }}
            onClick={() => setFiltersPanelOpen(false)}
            onKeyDown={(e) => e.key === 'Escape' && setFiltersPanelOpen(false)}
            aria-hidden="true"
          />
          <div
            role="dialog"
            aria-label="Project config filters"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.key === 'Escape' && setFiltersPanelOpen(false)}
            style={{
              position: 'fixed',
              top: 0,
              right: 0,
              bottom: 0,
              width: '100%',
              maxWidth: 400,
              zIndex: 1301,
              display: 'flex',
              flexDirection: 'column',
              background: palette.background.paper,
              color: palette.text.primary,
              borderLeft: `1px solid ${palette.divider}`,
              boxShadow: theme.shadows[8],
            }}
          >
            <div
              style={{
                padding: 20,
                borderBottom: `1px solid ${palette.divider}`,
                textAlign: 'left',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  fontWeight: 700,
                  fontSize: 18,
                }}
              >
                <FilterListIcon
                  style={{ color: palette.primary.main, fontSize: 22 }}
                />
                Project Config Filters
              </div>
              <p
                style={{
                  margin: '8px 0 0 0',
                  fontSize: 14,
                  color: palette.text.secondary,
                }}
              >
                Filter by level, cluster, application, fuel type, config
                type, and component.
              </p>
            </div>

            <div
              style={{
                flex: 1,
                overflow: 'auto',
                padding: 20,
                textAlign: 'left',
              }}
            >
              <label
                style={{
                  display: 'block',
                  fontSize: 11,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.05em',
                  color: palette.text.secondary,
                  marginBottom: 12,
                  textAlign: 'left',
                }}
              >
                Filters
              </label>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 16,
                }}
              >
                <Box>
                  <SearchableMultiSelect
                    label="Level"
                    options={filterOptions.levels}
                    selected={draftLevelFilter}
                    onChange={(value) => {
                      const v = value ?? [];
                      setDraftLevelFilter(v);
                      if (!v.some((l) => l.toUpperCase() === 'CLUSTERS')) setDraftClusterFilter([]);
                      if (!v.some((l) => l.toUpperCase() === 'APPLICATIONS')) {
                        setDraftApplicationFilter([]);
                        setDraftFuelTypeFilter([]);
                      }
                      if (!v.some((l) => l.toUpperCase() === 'FUEL')) setDraftFuelTypeFilter([]);
                    }}
                    emptyOptionLabel="—"
                  />
                </Box>

                {hasClusterLevelSelected && (
                  <Box>
                    <SearchableMultiSelect
                      label="Cluster"
                      options={filterOptions.clusters.map((c) => c.label)}
                      selected={filterOptions.clusters
                        .filter((c) => draftClusterFilter.includes(c.value))
                        .map((c) => c.label)}
                      onChange={(selectedLabels) => {
                        const values = filterOptions.clusters
                          .filter((c) => selectedLabels.includes(c.label))
                          .map((c) => c.value);
                        setDraftClusterFilter(values);
                      }}
                      placeholder={clusterLoading ? 'Loading clusters…' : 'Select clusters'}
                      emptyOptionLabel="—"
                    />
                    {needsClusterSelection && (
                      <FormHelperText error sx={{ mt: 0.5, mx: 0 }}>
                        Select at least one cluster to view cluster-level configs.
                      </FormHelperText>
                    )}
                  </Box>
                )}

                {showApplicationSection && (
                  <Box>
                    <SearchableMultiSelect
                      label="Application"
                      options={filterOptions.applications
                        .filter((a) => a.value)
                        .map((a) => a.value)}
                      selected={draftApplicationFilter}
                      onChange={(v) => {
                        setDraftApplicationFilter(v);
                        if ((v?.length ?? 0) === 0) setDraftFuelTypeFilter([]);
                      }}
                      placeholder={
                        hasFuelLevelSelected || hasFuelTypeChosen
                          ? 'Select application first (required for fuel type)'
                          : 'Select application'
                      }
                      emptyOptionLabel="—"
                    />
                    {needsApplicationSelection && (
                      <FormHelperText error sx={{ mt: 0.5, mx: 0 }}>
                        {hasFuelTypeChosen || hasFuelLevelSelected
                          ? 'Select at least one application (required when fuel type is selected).'
                          : 'Select at least one application to view application-level configs.'}
                      </FormHelperText>
                    )}
                  </Box>
                )}

                {hasFuelLevelSelected && (
                  <Box>
                    <SearchableMultiSelect
                      label="Fuel Type"
                      options={filterOptions.fuelTypes
                        .filter((f) => f.value && f.value !== 'All')
                        .map((f) => f.value)}
                      selected={draftFuelTypeFilter}
                      onChange={setDraftFuelTypeFilter}
                      placeholder={applicationRequiredBeforeFuel ? 'Select an application first' : 'Select fuel type'}
                      emptyOptionLabel="—"
                      disabled={applicationRequiredBeforeFuel}
                    />
                    {applicationRequiredBeforeFuel && (
                      <FormHelperText sx={{ mt: 0.5, mx: 0, color: 'text.secondary' }}>
                        Select an application above first.
                      </FormHelperText>
                    )}
                    {!applicationRequiredBeforeFuel && needsFuelSelection && (
                      <FormHelperText error sx={{ mt: 0.5, mx: 0 }}>
                        Select at least one fuel type to view fuel-level configs.
                      </FormHelperText>
                    )}
                  </Box>
                )}

                {includeConfigType && filterOptions.configTypes.length > 0 && (
                  <SearchableMultiSelect
                    label="Config Type"
                    options={filterOptions.configTypes}
                    selected={draftConfigTypes}
                    onChange={setDraftConfigTypes}
                    emptyOptionLabel="—"
                  />
                )}
                {filterOptions.configKeys.length > 0 && (
                  <SearchableMultiSelect
                    label="Config Key"
                    options={filterOptions.configKeys}
                    selected={draftConfigKeyFilter}
                    onChange={setDraftConfigKeyFilter}
                    placeholder="Select config key"
                    emptyOptionLabel="—"
                  />
                )}
                {filterOptions.components.length > 0 && (
                  <SearchableMultiSelect
                    label="Component"
                    options={filterOptions.components}
                    selected={draftComponentFilter}
                    onChange={setDraftComponentFilter}
                    emptyOptionLabel="—"
                  />
                )}
              </div>
            </div>

            {validationErrors.length > 0 && (
              <Box sx={{ px: 2, py: 1.5, bgcolor: VALIDATION_BANNER_BG, color: 'warning.dark', border: '1px solid', borderColor: VALIDATION_BANNER_BORDER, borderRadius: 1, mx: 2, mb: 1 }}>
                <Typography variant="caption" component="div" sx={{ fontWeight: 600 }}>
                  Complete the following to apply:
                </Typography>
                <Typography component="ul" variant="caption" sx={{ m: 0, pl: 2 }}>
                  {validationErrors.map((msg, i) => (
                    <li key={i}>{msg}</li>
                  ))}
                </Typography>
              </Box>
            )}
            <div
              style={{
                padding: 16,
                borderTop: `1px solid ${palette.divider}`,
                background: palette.action.hover,
                display: 'flex',
                gap: 8,
              }}
            >
              <button
                type="button"
                onClick={handleResetAndClose}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  background: 'transparent',
                  color: palette.text.primary,
                  border: `1px solid ${palette.divider}`,
                  borderRadius: 8,
                  cursor: 'pointer',
                }}
              >
                Reset All
              </button>
              <button
                type="button"
                onClick={handleApplyFilters}
                disabled={!canApplyFilters}
                title={!canApplyFilters ? validationErrors.join(' ') : undefined}
                style={{
                  flex: 1,
                  padding: '8px 16px',
                  fontSize: 14,
                  fontWeight: 600,
                  background: canApplyFilters ? palette.primary.main : palette.action.disabledBackground,
                  color: canApplyFilters ? palette.primary.contrastText : palette.action.disabled,
                  border: 'none',
                  borderRadius: 8,
                  cursor: canApplyFilters ? 'pointer' : 'not-allowed',
                }}
              >
                Apply Filters
              </button>
            </div>
          </div>
        </>
      )}
    </>
  );
}

/** Renders only the active filter chips + Reset All; use below search bar when activeFiltersBelow is true on ProjectConfigFilter. */
export function ProjectConfigFilterActiveChips(props: ProjectConfigFilterProps) {
  const {
    filterOptions,
    levelFilter = [],
    clusterFilter = [],
    applicationFilter = [],
    fuelTypeFilter = [],
    configTypes = [],
    configKeyFilter = [],
    componentFilter = [],
    onLevelFilterChange,
    onClusterFilterChange,
    onApplicationFilterChange,
    onFuelTypeFilterChange,
    onConfigTypesChange,
    onConfigKeyFilterChange,
    onComponentFilterChange,
    onResetAll,
    includeConfigType = true,
    effectiveApiScope,
  } = props;
  const scopeActive =
    effectiveApiScope === 'cluster' && (clusterFilter ?? []).length > 0
      ? true
      : effectiveApiScope === 'product_type' && (applicationFilter ?? []).length > 0
        ? true
        : effectiveApiScope === 'fuel_type' && (fuelTypeFilter ?? []).length > 0
          ? true
          : !effectiveApiScope &&
            ((clusterFilter ?? []).length > 0 || (applicationFilter ?? []).length > 0 || (fuelTypeFilter ?? []).length > 0);
  const hasActiveFilters =
    (levelFilter ?? []).length > 0 ||
    scopeActive ||
    (includeConfigType && (configTypes ?? []).length > 0) ||
    (configKeyFilter ?? []).length > 0 ||
    (componentFilter ?? []).length > 0;
  const toggleConfigType = React.useCallback(
    (value: string, add: boolean) => {
      const next = add
        ? (configTypes.includes(value) ? configTypes : [...configTypes, value])
        : configTypes.filter((x) => x !== value);
      onConfigTypesChange(next);
    },
    [configTypes, onConfigTypesChange]
  );
  if (!hasActiveFilters) return null;
  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1,
        minHeight: 32,
        width: '100%',
        justifyContent: 'flex-start',
        px: 0.5,
      }}
    >
      <Typography
        variant="caption"
        sx={{
          fontWeight: 700,
          color: 'text.secondary',
          textTransform: 'uppercase',
          letterSpacing: '0.05em',
          mr: 0.5,
        }}
      >
        Active Filters:
      </Typography>
      {levelFilter.length > 0 &&
        (levelFilter.length <= 2
          ? levelFilter.map((lv) => (
              <Chip
                key={`level-${lv}`}
                size="small"
                label={`Level: ${lv}`}
                onDelete={() => onLevelFilterChange(levelFilter.filter((x) => x !== lv))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip title={levelFilter.join(', ')} placement="top">
                <Chip
                  size="small"
                  label={`Level: ${levelFilter.length} selected`}
                  onDelete={() => onLevelFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      {clusterFilter.length > 0 &&
        (clusterFilter.length <= 2
          ? clusterFilter.map((v) => (
              <Chip
                key={`cluster-${v}`}
                size="small"
                label={`Cluster: ${filterOptions.clusters.find((c) => c.value === v)?.label ?? v}`}
                onDelete={() => onClusterFilterChange(clusterFilter.filter((x) => x !== v))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip
                title={clusterFilter.map((v) => filterOptions.clusters.find((c) => c.value === v)?.label ?? v).join(', ')}
                placement="top"
              >
                <Chip
                  size="small"
                  label={`Cluster: ${clusterFilter.length} selected`}
                  onDelete={() => onClusterFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      {applicationFilter.length > 0 &&
        (applicationFilter.length <= 2
          ? applicationFilter.map((v) => (
              <Chip
                key={`app-${v}`}
                size="small"
                label={`Application: ${v}`}
                onDelete={() => onApplicationFilterChange(applicationFilter.filter((x) => x !== v))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip title={applicationFilter.join(', ')} placement="top">
                <Chip
                  size="small"
                  label={`Application: ${applicationFilter.length} selected`}
                  onDelete={() => onApplicationFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      {fuelTypeFilter.length > 0 &&
        (fuelTypeFilter.length <= 2
          ? fuelTypeFilter.map((v) => (
              <Chip
                key={`fuel-${v}`}
                size="small"
                label={`Fuel: ${v}`}
                onDelete={() => onFuelTypeFilterChange(fuelTypeFilter.filter((x) => x !== v))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip title={fuelTypeFilter.join(', ')} placement="top">
                <Chip
                  size="small"
                  label={`Fuel: ${fuelTypeFilter.length} selected`}
                  onDelete={() => onFuelTypeFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      {includeConfigType &&
        configTypes.map((v) => (
          <Chip
            key={`t-${v}`}
            size="small"
            label={`Type: ${v}`}
            onDelete={() => toggleConfigType(v, false)}
            sx={{ height: 28 }}
          />
        ))}
      {configKeyFilter.length > 0 &&
        (configKeyFilter.length <= 2
          ? configKeyFilter.map((v) => (
              <Chip
                key={`key-${v}`}
                size="small"
                label={`Config Key: ${v}`}
                onDelete={() => onConfigKeyFilterChange(configKeyFilter.filter((x) => x !== v))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip title={configKeyFilter.join(', ')} placement="top">
                <Chip
                  size="small"
                  label={`Config Key: ${configKeyFilter.length} selected`}
                  onDelete={() => onConfigKeyFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      {componentFilter.length > 0 &&
        (componentFilter.length <= 2
          ? componentFilter.map((v) => (
              <Chip
                key={`comp-${v}`}
                size="small"
                label={`Component: ${v}`}
                onDelete={() => onComponentFilterChange(componentFilter.filter((x) => x !== v))}
                sx={{ height: 28 }}
              />
            ))
          : (
              <Tooltip title={componentFilter.join(', ')} placement="top">
                <Chip
                  size="small"
                  label={`Component: ${componentFilter.length} selected`}
                  onDelete={() => onComponentFilterChange([])}
                  sx={{ height: 28 }}
                />
              </Tooltip>
            ))}
      <Typography
        component="button"
        variant="caption"
        sx={{
          fontWeight: 600,
          color: 'primary.main',
          textDecoration: 'underline',
          cursor: 'pointer',
          border: 0,
          background: 'none',
          p: 0,
        }}
        onClick={onResetAll}
      >
        Reset All
      </Typography>
    </Box>
  );
}
